import React from "react";

import {WithDeleteDialogZzFlatButton, ZzFlatButton, ZzFlatButtonRoute} from "core/components/buttons/buttons";
import PageAncestor from "core/common/page/pageAncestor";
import Toolmenu from "core/components/toolmenu/toolmenu";
import mdEntityService from "core/metamodel/mdEntityService";
import {ErrorComp} from "core/components/errorComp/errorComp";
import {IDomainObject, lovLabel, lovValue, preSave} from "core/form/formUtils";
import ConfirmationDialog from "core/components/confirmationDialog/confirmationDialog";
import {MODULE, PageProps, PERMISSION, SECURABLE, SECURABLE_TYPE} from "core/metamodel/metaConstant";
import "invoice/page/invoiceDetail.less";
import {observer} from "mobx-react";
import {action, ObservableMap} from "mobx";
import uiService from "core/common/service/uiService";
import MdEntity from "core/metamodel/mdEntity";
import {Shipment, SHIPMENTDIRECTION, SHIPMENTSTATUS_GRID_CSS, ShipmentVisit, SHIPMENTVISITSTEP} from "shipment/shipmentDomain";
import shipmentService from "shipment/shipmentService";
import {loc} from "core/common/config/locales";
import ShipmentVisitDriverForm from "shipment/component/shipmentVisitDriverForm";
import securityService from "core/security/securityService";
import {PAGE} from "routes";
import gridService from "core/grid/gridService";
import Grid, {ClientGrid, FILTEROPERATOR, GridLocation, GridParams} from "core/grid/domain/grid";

import 'flag-icon-css/css/flag-icon.css';
import {RESTRICTION} from "core/security/securityDomain";
import ShipmentVisitPrepareForm from "shipment/component/shipmentVisitPrepareForm";
import SelectSearchDialog from "core/components/fields/selectSearchDialog";
import GridConfigCondition from "core/grid/domain/gridConfigCondition";


const visitNewShipmentGridLocation = 'visitNewShipmentList';


@observer
export default class ShipmentVisitDetail extends PageAncestor<PageProps, undefined> {

  static module = MODULE.shipment;
  static title = loc.i18n`Shipment visit`;
  static icon = 'blind';
  static protectedPage = true;


  static fetchData(routerParams, query) {
    console.log("ShipmentVisitDetail#fetchData(%O)", query);

    let metadataPromise = mdEntityService.fetchEntityMetadata(['ShipmentVisit'], ["Shipment", {entity: "SHIPMENTDIRECTION", lov: true}]);
    let shipmentVisitPromise: Promise<ShipmentVisit>;
    let gridPromise = gridService.fetchGrids(visitNewShipmentGridLocation);
    let shipmentGridSearch: Promise<any>;
    let shipmentPromise: Promise<Shipment>;

    if (routerParams.id === 'new') {
      shipmentVisitPromise = Promise.resolve(new ShipmentVisit({
        $new: true
      }));
      if (query.gridId) {
        shipmentGridSearch = gridService.innerSearch(query.gridId, query);
      } else if (query.shipmentId) {
        shipmentPromise = shipmentService.readShipment(query.shipmentId);
      }

    } else {
      shipmentVisitPromise = shipmentService.readShipmentVisit(routerParams.id, true);
    }

    if (!shipmentGridSearch) shipmentGridSearch = Promise.resolve(null);
    if (!shipmentPromise) shipmentPromise = Promise.resolve(null);


    return Promise.all([metadataPromise, shipmentVisitPromise, gridPromise, shipmentGridSearch, shipmentPromise])
      .then(action("action-fetcgdara", ([entityMap, shipmentVisitObject, gridLocationMap, gridSearchResult, shipment]: [ObservableMap<MdEntity>, ShipmentVisit, ObservableMap<GridLocation>, any, Shipment]) => {
        let shipmentdirectionEntity = entityMap.get("SHIPMENTDIRECTION");

        shipmentVisitObject.$shipmentVisitShipmentsGrid = new ClientGrid<Shipment>(gridLocationMap.get(visitNewShipmentGridLocation), "shipments", undefined,
          {shipmentStatusIcon: (sh) => lovLabel(sh.shipmentStatus)}, {shipmentStatusIcon: (sh) => SHIPMENTSTATUS_GRID_CSS[lovValue(sh.shipmentStatus)]});

        shipmentService.shipmentVisitObject = shipmentVisitObject.initAfterRead(routerParams.id === 'new');

        if (routerParams.id === 'new') {
          shipmentVisitObject.step = entityMap.get("SHIPMENTVISITSTEP").getLovItem(SHIPMENTVISITSTEP.PREPARED_VISIT);
        } else {
          if (lovValue(shipmentVisitObject.step) === SHIPMENTVISITSTEP.VEHICLE_ARRIVED || lovValue(shipmentVisitObject.step) === SHIPMENTVISITSTEP.PREPARED_VISIT) {
            shipmentVisitObject.$showSmsToDriver = true;
          }
        }

        if (gridSearchResult) {
          shipmentVisitObject.shipments = gridSearchResult.data.rows.map(r => new Shipment(r.rowObject)).filter(sh => !sh.shipmentVisit)
        } else if (shipment) {
          shipmentVisitObject.shipments = [shipment];
        }

        if (gridSearchResult || shipment) {
          shipmentVisitObject.directionOutgoing = shipmentVisitObject.shipments.some(sh => sh.shipmentDirection === SHIPMENTDIRECTION.OUTGOING);
          shipmentVisitObject.directionIncoming = shipmentVisitObject.shipments.some(sh => sh.shipmentDirection === SHIPMENTDIRECTION.INCOMING);
          let shipmentWithSpz = shipmentVisitObject.shipments.filter(sh => !!sh.registrationNumber).shift();
          if (shipmentWithSpz) shipmentVisitObject.registrationNumber = shipmentWithSpz.registrationNumber;
        }

        if (shipmentVisitObject.directionOutgoing) {
          shipmentVisitObject.$direction = shipmentdirectionEntity.lovItems.find(lov => lov.value === SHIPMENTDIRECTION.OUTGOING);
        } else if (shipmentVisitObject.directionIncoming) {
          shipmentVisitObject.$direction = shipmentdirectionEntity.lovItems.find(lov => lov.value === SHIPMENTDIRECTION.INCOMING);
        }
        shipmentVisitObject.$syncRegistrationNumber = shipmentVisitObject.shipments && shipmentVisitObject.shipments.some(sh => sh.registrationNumber !== shipmentVisitObject.registrationNumber);

        if (shipmentVisitObject.shipments) {
          return shipmentVisitObject.$shipmentVisitShipmentsGrid.search(shipmentVisitObject);
        }
      }));
  }

  static willTransitionFrom(transition, component, callback) {
    console.log('shipmentVisitDetail willTransitionFrom: transition = %O, component = %O', transition, component);
    if (component.selectVisitDialogRef && component.selectVisitDialogRef.dialogOpen) {
      console.log("canceling transition");
      transition.cancel();
      component.context.router.goBack();
    } else {
      super.willTransitionFrom(transition, component, callback);
    }
  }

  private selectVisitDialogRef: SelectSearchDialog;
  selectVisitDialogRefHandler = (comp) => this.selectVisitDialogRef = comp;


  componentWillUnmount() {
    shipmentService.shipmentVisitObject = null;
  }


  customValidate = (shipmentVisitObject: ShipmentVisit) => {
    let errors = [];

    if (!shipmentVisitObject.directionIncoming && !shipmentVisitObject.directionOutgoing) {
      errors.push({message: "Alespoň jeden směr musí být zaškrtnut."})
    }

    if ([SHIPMENTVISITSTEP.VEHICLE_ARRIVED, SHIPMENTVISITSTEP.VEHICLE_ENTERED].includes(lovValue(shipmentVisitObject.step)) && !shipmentVisitObject.driverPhone) {
      errors.push({message: "Driver phone is required."})
    }


    return errors;
  };


  findRootObject() {
    return shipmentService.shipmentVisitObject;
  }

  @action onSaveFromSubmit = (shipmentVisitObject: ShipmentVisit, afterValidateFn: () => void): Promise<any> => {
    if (!securityService.hasRight(SECURABLE.shipmentVisitDetail.name, (shipmentVisitObject.$new) ? PERMISSION.New : PERMISSION.Update)) {
      return Promise.resolve(null);
    }

    console.log('onSave shipmentVisitId = %s, $new = %s', shipmentVisitObject.shipmentVisitId, shipmentVisitObject.$new);
    uiService.showToolbarActionFlag();

    if (shipmentVisitObject.driverPhone === '+') shipmentVisitObject.driverPhone = undefined;

    let result = preSave(shipmentVisitObject, this.customValidate, true);
    if (!result) {
      return Promise.resolve(null);
    }

    if (afterValidateFn) {
      afterValidateFn();
    }

    let gridParams: GridParams = shipmentVisitObject.$shipmentVisitShipmentsGrid.createFullParams();

    return shipmentService.saveShipmentVisit(shipmentVisitObject, shipmentVisitObject.$syncRegistrationNumber,
      shipmentVisitObject.$syncShipmentStatus, lovValue(shipmentVisitObject.$syncShippedFromPlace), gridParams,
      shipmentVisitObject.$visitsToDelete ? shipmentVisitObject.$visitsToDelete.map(v => v.shipmentVisitId) : undefined )
      .then(action("saveShipmentVisit-action", (shipmentVisitJson: IDomainObject) => {
        let wasNew = shipmentVisitObject.cleanAfterSave(shipmentVisitJson, true, "$direction");
        shipmentVisitObject.$previousShipments = undefined;
        shipmentVisitObject.$visitsToDelete = undefined;
        shipmentVisitObject.$syncShippedFromPlace = undefined;
        shipmentVisitObject.$syncShipmentStatus = undefined;
        if (wasNew) {
          this.context.router.replaceWith("shipmentVisitDetail", {id: shipmentVisitObject.shipmentVisitId});
        }

        uiService.hideToolbarActionFlag();

        return Promise.resolve(null);
      }), error => {
        shipmentVisitObject.$errors = error.getMessages();
      });

  };


  onAdd = (evt) => {
    console.log('onAdd');
    let query = {};
    this.context.router.transitionTo("shipmentVisitDetail", {id: 'new'}, query);
  };


  @action onDelete = (evt) => {
    console.log('onDelete');

    uiService.showToolbarActionFlag();

    shipmentService.deleteShipmentVisit(shipmentService.shipmentVisitObject.shipmentVisitId)
      .then(response => {
        uiService.hideToolbarActionFlag();
        this.goBackOrHome();
      }, error => {
        shipmentService.shipmentVisitObject.$errors = error.getMessages();
      });
  };

  @action onMerge = (evt) => {
    console.log('onMerge');
    const shipmentVisitEntity = mdEntityService.getEntity('ShipmentVisit');
    const shipmentVisitObject = shipmentService.shipmentVisitObject;

    new Promise((resolve, reject) => {
      return this.selectVisitDialogRef.open(resolve, reject, (grid: Grid) => {
        if (!grid.conditionArray) grid.conditionArray = [];
        // pouze stavy PREPARED_VISIT a VEHICLE_ARRIVED
        grid.conditionArray.push(new GridConfigCondition(grid.$gridLocationRef, {
          field: shipmentVisitEntity.getField("step"),
          operator: FILTEROPERATOR.IN,
          values: [SHIPMENTVISITSTEP.PREPARED_VISIT, SHIPMENTVISITSTEP.VEHICLE_ARRIVED]
        }));
        // vyloucime jiz pridane
        grid.conditionArray.push(new GridConfigCondition(grid.$gridLocationRef, {
          field: shipmentVisitEntity.getField("shipmentVisitId"),
          operator: FILTEROPERATOR.NOTIN,
          values: [...(shipmentVisitObject.$visitsToDelete || []).map(sv => sv.shipmentVisitId), ...(shipmentVisitObject.shipmentVisitId?[shipmentVisitObject.shipmentVisitId]:[]) ]
        }));
      });
    }).then( (visit: ShipmentVisit) => {
      console.log("merging visit id %s", visit.shipmentVisitId);
      return shipmentService.readShipmentVisit(visit.shipmentVisitId, true)
    }).then((visitWithShipments: ShipmentVisit) => {
      shipmentVisitObject.shipments = [ ...shipmentVisitObject.shipments || [], ...visitWithShipments.shipments.filter(sh => sh.shipmentDirection == lovValue(shipmentVisitObject.$direction))];
      shipmentVisitObject.$visitsToDelete = [ ...shipmentVisitObject.$visitsToDelete || [], visitWithShipments];
      shipmentVisitObject.$dirty = true;
      return shipmentVisitObject.$shipmentVisitShipmentsGrid.search(shipmentVisitObject);
    }).catch( (e) => {});

  };

  render() {

    const {
      query,
    } = this.props;

    let shipmentVisitObject = shipmentService.shipmentVisitObject;
    let shipmentVisitEntity = mdEntityService.getEntity('ShipmentVisit');

    const propsForCreateForm = {
      dataObject: shipmentVisitObject,
      rootObject: shipmentVisitObject,
      entity: shipmentVisitEntity,
      entities: mdEntityService.entityMap
    };

    console.log('shipmentVisitDetail render shipmentVisitId = %s, $new = %s', shipmentVisitObject.shipmentVisitId, shipmentVisitObject.$new);

    return (

      <main className="main-content" style={{display: 'flex', flexDirection: 'column'}}>
        {this._createToolMenu(shipmentVisitObject, query.gridLocation)}
        <ErrorComp messages={shipmentVisitObject.$errors}/>
        <ConfirmationDialog ref={this.dirtyDialogRefHandler}/>
        <ConfirmationDialog ref={this.deleteDialogRefHandler} okButtonText={loc.i18n`Yes`} cancelButtonText={loc.i18n`No`}/>

        { (!securityService.currentUser.hasRestriction(RESTRICTION.SIMPLE_SHIPMENT_VISIT)) &&
            <SelectSearchDialog ref={this.selectVisitDialogRefHandler} gridLocation="shipmentVisitSearch2" masterId={lovValue(shipmentVisitObject.$direction)}
                                showGridConfig={false} title="Select Visit to merge and delete" />
        }

        {
          (!securityService.currentUser.hasRestriction(RESTRICTION.SIMPLE_SHIPMENT_VISIT)) ? (
            <ShipmentVisitPrepareForm {...propsForCreateForm} onSubmit={this.onSaveFromSubmit} disabled={lovValue(shipmentVisitObject.step) === SHIPMENTVISITSTEP.VEHICLE_LEFT}/>
          ) : ((shipmentVisitObject.$new || lovValue(shipmentVisitObject.step) === SHIPMENTVISITSTEP.PREPARED_VISIT) ? (
            <ShipmentVisitDriverForm {...propsForCreateForm} onSubmit={this.onSaveFromSubmit} />
          ) : (
            <ShipmentVisitPrepareForm {...propsForCreateForm} disabled={true}/>
          ))
        }

      </main>
    );
  }


  _createToolMenu(shipmentVisitObject: ShipmentVisit, gridLocation: string) {
    return (
      <Toolmenu chainGridLocation={gridLocation} backAction={this.onBack} backDisabled={History.length <= 1} entityName="ShipmentVisit" entityId={shipmentVisitObject.shipmentVisitId} historize>
          <ZzFlatButtonRoute routeName="shipmentVisitDetail" params={{id: 'new'}} label={loc.i18n`New Shipment visit`} permission={PERMISSION.New} disabled={shipmentVisitObject.$new}/>
          <ZzFlatButtonRoute routeName="shipmentVisitList" label={loc.i18n`Back to visits`}/>
          <WithDeleteDialogZzFlatButton entityName="ShipmentVisit" pageAncestor={this} onClick={this.onDelete} disabled={shipmentVisitObject.$new} label={loc.i18n`Delete`} permission={PERMISSION.Delete}/>
          {(!securityService.currentUser.hasRestriction(RESTRICTION.SIMPLE_SHIPMENT_VISIT)) && !!shipmentVisitObject.$direction && <ZzFlatButton onClick={this.onMerge} disabled={lovValue(shipmentVisitObject.step) === SHIPMENTVISITSTEP.VEHICLE_LEFT} label={loc.i18n`Merge Visit`} permission={PERMISSION.Delete}/>}
      </Toolmenu>
    );
  }


}




// WEBPACK FOOTER //
// ./src/shipment/page/shipmentVisitDetail.tsx